import { useMemo, useState } from 'react';

import { AnnouncementBar } from '@/components/AnnouncementBar';
import { Carousel } from '@/components/Carousel';
import { DayIndicator } from '@/components/DayIndicator/DayIndicator';
import { GradientSpacer } from '@/components/GradientSpacer';
import { Header } from '@/components/Header';
import { ItineraryBlockCard } from '@/components/ItineraryBlockCard';
import { NameTag } from '@/components/NameTag';
import { Navbar } from '@/components/Navbar';
import { Paragraph } from '@/components/Paragraph';
import { ProgramBlock } from '@/components/ProgramBlock';
import { Spacer } from '@/components/Spacer';
import { TransportCard } from '@/components/TransportCard';
import { useItineraryContext } from '@/context/ItineraryContext';
import { CenteredText, DualSection, EnforcePageWidth } from '@/layout/PageLayout';
import { filterItineraryBlocksByType } from '@/utils/filterItineraryBlocksByType';
import { sortItineraryBlockByDate } from '@/utils/sortItineraryBlockByDate';

import { StyledTravelMapImage, StyledBlockCardWrapper } from './styles';

function ProgramPage() {
  const [focusedCarouselIndex, setFocusedCarouselIndex] = useState(0);
  const { data, isLoading } = useItineraryContext();
  const itinerary = data;
  const itineraryBlocks = data?.ItineraryBlocks;
  const destination = data?.Destination;

  const mainContent = useMemo(
    () => destination?.Content.find((content) => content.type.toLowerCase() === 'main'),
    [destination],
  );

  const backgroundUrl = useMemo(
    () => mainContent?.RichContent?.filter((content) => content.subType?.toLowerCase() === 'header')[1]?.path,
    [mainContent],
  );
  const travelGuide = useMemo(
    () => itinerary?.Opportunity.owner.Content?.find((content) => content.type.toLowerCase() === 'main'),
    [itinerary],
  );
  const travelGuideImage = useMemo(() => travelGuide?.RichContent?.[0]?.path ?? '', [travelGuide]);
  const cards = useMemo(
    () =>
      itineraryBlocks?.map((block, index) => ({
        id: block.id,
        element: (
          <StyledBlockCardWrapper $isFocused={focusedCarouselIndex === index}>
            <ItineraryBlockCard itineraryBlock={block} />
          </StyledBlockCardWrapper>
        ),
      })),
    [focusedCarouselIndex, itineraryBlocks],
  );

  const regularItineraryBlocksSorted = useMemo(
    () => itineraryBlocks && sortItineraryBlockByDate(filterItineraryBlocksByType(itineraryBlocks, 'regular')),
    [itineraryBlocks],
  );

  // useEffect(() => {
  //   console.log('itineraryBlocks', itineraryBlocks);
  //   console.log('regularItineraryBlocksSorted', regularItineraryBlocksSorted);
  // }, [itineraryBlocks, regularItineraryBlocksSorted]);

  if (!destination && !isLoading) {
    throw Error('No destination found');
  }

  if (!regularItineraryBlocksSorted) {
    throw Error('No Itinerary blocks found');
  }

  return (
    <>
      <AnnouncementBar />
      <Navbar variant="primary" />
      <Header title="The highlights of your trip" backgroundUrl={backgroundUrl} />
      <EnforcePageWidth>
        <Spacer size="xl" />
        <DualSection>
          <div>
            <h2>A personal touch</h2>
            <Paragraph>{itinerary?.personalMessage}</Paragraph>
            {travelGuide && (
              <NameTag
                mt={8}
                name={travelGuide.header || ''}
                subtitle={travelGuide.subHeader || ''}
                imageUrl={travelGuideImage}
              />
            )}
          </div>
          <StyledTravelMapImage
            src="https://png.pngtree.com/png-vector/20230929/ourmid/pngtree-norway-map-on-green-paper-creases-png-image_10049190.png"
            alt="madeira route"
          />
        </DualSection>
        <CenteredText>
          <h2>Your journey summarised</h2>
        </CenteredText>
      </EnforcePageWidth>
      {cards && (
        <Carousel
          maxCardHeightPx={900}
          cardWrapperHeightPx={800}
          startAtIndex={Math.floor(cards.length / 2)}
          onFocusIndexChange={setFocusedCarouselIndex}
          cards={cards}
          alignControls="right"
          showOverview={false}
        />
      )}
      <CenteredText>
        <h2>Your programme from day to day</h2>
      </CenteredText>
      <DayIndicator itineraryBlocks={regularItineraryBlocksSorted} />
      {regularItineraryBlocksSorted?.map((block) => <ProgramBlock itineraryBlock={block} key={block.id} />)}
      <GradientSpacer variant="bottom" />

      {/* JOUW VERVOER..... */}
      <TransportCard />
    </>
  );
}

export { ProgramPage };
